import React, { useEffect, useState } from 'react';
import './ChangeTempoPopup.scss';
import Rodal from '../../../../components/Rodal/Rodal';
import { ModalBody, ModalHeader } from 'reactstrap';
import { ChangeTempoForm } from './ChangeTempoForm/ChangeTempoForm';
import { TimelineTempo } from '../../../../slices/marketplace/timeline/models/enums/timeline-tempo.enum';
import ChangeTempoFeePaymentComponent from '../../../../components/PaymentDialog/ChangeTempoFeePaymentComponent/ChangeTempoFeePaymentComponent';
import { useDispatch } from 'react-redux';
import { useStateSelector } from '../../../../store/selectors';
import { LoadMarketplacePricing } from '../../../../slices/marketplace/pricing/load-marketplace-pricing.thunk';
import {
    PricesCalculationHelper,
    ServicePriceDetails,
} from '../../../../utils/pricesCalculationHelper';
import { LevelOfService } from '../../../../slices/marketplace/timeline/models/enums/level-of-service.enum';
import createNotification from '../../../../utils/createNotification';

interface ChangeTempoPopupProps {
    isVisible: boolean;
    setIsVisible(value: boolean): void;
    fetchData: () => void;
    checkEventOrder: (newTempo: TimelineTempo) => void;
}

const ChangeTempoPopup = (props: ChangeTempoPopupProps) => {
    const axios = useStateSelector((state) => state.core.axios);
    const engagement = useStateSelector(
        (state) => state.timelines.currentTimeline
    );
    const [isCheckoutVisible, setIsCheckoutVisible] = useState(false);
    const dispatch = useDispatch() as any;
    const marketplacePricing = useStateSelector(
        (state) => state.marketplacePricing.pricing
    );
    const [tempotPricingDetails, setTempotPricingDetails] =
        useState<ServicePriceDetails>();
    const [selectedTempo, setSelectedTempo] = useState<TimelineTempo>();
    const isFree =
        engagement.levelOfService ===
            LevelOfService.StrategicFinancialAdvisoryService ||
        (engagement?.engagementProcess?.buyers?.filter((b) => b.isActive)
            .length ?? 0) === 0;

    useEffect(() => {
        dispatch(LoadMarketplacePricing(engagement.id));
    }, []);

    useEffect(() => {
        calculateChangeTempoFee();
    }, [marketplacePricing]);

    const calculateChangeTempoFee = () => {
        const levelOfService = engagement.levelOfService;

        const brokreagePricing =
            PricesCalculationHelper.getBrokeragePricingByLevelOfService(
                levelOfService,
                marketplacePricing
            );

        const fteDvmsCount = engagement?.engagementProcess?.startFteDvm || 0;

        const priceDetails = PricesCalculationHelper.getChengeTempoFeePrice(
            brokreagePricing,
            fteDvmsCount
        );
        setTempotPricingDetails(priceDetails);
    };

    const onSuccessfulPayment = () => {
        props.fetchData();
        props.checkEventOrder(selectedTempo);
        props.setIsVisible(false);
    };

    const handleSubmit = (selectedTempo: TimelineTempo) => {
        setSelectedTempo(selectedTempo);

        if (!isFree) {
            setIsCheckoutVisible(true);
            return;
        }

        axios
            .put(`/api/Marketplace/Engagements/${engagement.id}/Tempo`, {
                tempo: selectedTempo,
            })
            .then((response) => {
                if (response.status === 200) {
                    onSuccessfulPayment();
                } else {
                    createNotification(
                        response.data?.title ??
                            (response as any)?.message ??
                            'An unexpected error occured. Please try again later',
                        'error'
                    );
                }
            })
            .catch((error) => {
                createNotification(
                    'An unexpected error occured. Please try again later',
                    'error'
                );
            });
    };

    return (
        <div className="change-tempo-popup-container">
            <Rodal
                visible={props.isVisible && !isCheckoutVisible}
                onClose={() => props.setIsVisible(false)}
                animation="slideUp"
                className="change-tempo-popup"
                showMask={true}
                width={400}
                showCloseButton={true}>
                <ModalHeader>
                    <b>Change Tempo</b>
                </ModalHeader>
                <ModalBody>
                    <ChangeTempoForm
                        currentTempo={engagement.tempo}
                        onClose={() => {
                            props.setIsVisible(false);
                        }}
                        onSubmit={handleSubmit}
                        totalFeeAmount={tempotPricingDetails?.totalPrice}
                        levelOfService={engagement.levelOfService}
                        isFree={isFree}
                    />
                </ModalBody>
            </Rodal>

            {isCheckoutVisible ? (
                <ChangeTempoFeePaymentComponent
                    isVisible={isCheckoutVisible}
                    setIsVisible={setIsCheckoutVisible}
                    paymentSuccess={() => onSuccessfulPayment()}
                    engagementId={engagement.id}
                    feeAmount={tempotPricingDetails?.pricePerItem}
                    fteDvms={tempotPricingDetails?.fteDvms}
                    totalPrice={tempotPricingDetails?.totalPrice}
                    selectedTempo={selectedTempo}
                />
            ) : (
                <></>
            )}
        </div>
    );
};

export default ChangeTempoPopup;
